import './InvitedRelativesCard.scss';

import { faUserPlus } from '@fortawesome/pro-solid-svg-icons';

import { BIOLOGICAL_SEX_LABELS } from '@customTypes/constants';
import type { FVTReferralDetails } from '@customTypes/referrals';
import { pluralize } from '@helpers/string';
import { formatName, getRelationshipLabel } from '@helpers/subject';
import { formatLongDate } from '@utils/helpers';

import { InvitedRelativesEmptyState } from './InvitedRelativesEmptyState';
import { InviteRelativesButton } from './InviteRelativesButton';

type InvitedRelativesCardProps = {
  referral: FVTReferralDetails;
};

export const InvitedRelativesCard: React.FC<InvitedRelativesCardProps> = props => {
  const { referral } = props;

  const {
    family_members: familyMembers,
    is_proband: isProband,
    referred_by: referredBy,
    submitted_at: submittedAt,
  } = referral;

  const familyMembersCount = familyMembers.length;
  const referralDate = formatLongDate(submittedAt);

  const relativeTerm = pluralize({
    count: familyMembersCount,
    singular: 'relative',
    plural: 'relatives',
  });

  const referrerType = isProband ? 'provider' : 'family member';
  let referrerName: string | null = null;

  if (isProband) {
    referrerName = formatName({
      firstName: referredBy?.first_name,
      lastName: referredBy?.last_name,
    });
  }

  return (
    <section className='fvt-invited-card bg-white border rounded-3 p-4' aria-labelledby='fvt-invited-card-title'>
      {familyMembersCount > 0 ? (
        <>
          <p id='fvt-invited-card-title' className='fvt-invited-card__summary fw-medium text-primary'>
            {`You've invited ${familyMembersCount} ${relativeTerm}.`}
          </p>

          <p className='fvt-invited-card__lead text-secondary'>
            For privacy, we don&apos;t show whether each relative has opened or accepted their invitation. You can
            invite more relatives any time.
          </p>

          <div className='fvt-invited-card__table-wrap'>
            <table className='fvt-invited-card__table'>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Relationship</th>
                  <th>Sex</th>
                  <th>Email</th>
                  <th>Sent</th>
                </tr>
              </thead>

              <tbody>
                {familyMembers.map(familyMember => (
                  <tr key={familyMember.uuid}>
                    <td data-label='Name' data-openreplay-sanitize>
                      {familyMember.first_name} {familyMember.last_name}
                    </td>
                    <td data-label='Relationship' data-openreplay-sanitize>
                      {getRelationshipLabel(familyMember.relationship)}
                    </td>
                    <td data-label='Sex' data-openreplay-sanitize>
                      {BIOLOGICAL_SEX_LABELS[familyMember.sex] ?? familyMember.sex}
                    </td>
                    <td data-label='Email' data-openreplay-sanitize>
                      {familyMember.email ??
                        (familyMember.guardian?.email ? `${familyMember.guardian.email} (guardian)` : '-')}
                    </td>
                    <td data-label='Sent'>{formatLongDate(familyMember.invited_at) ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className='fvt-invited-card__actions'>
            <InviteRelativesButton label='Invite more relatives' icon={faUserPlus} />
          </div>
        </>
      ) : (
        <InvitedRelativesEmptyState />
      )}

      <footer className='fvt-invited-card__referrer' data-openreplay-sanitize>
        You were referred by your {referrerType}
        {referrerName ? `, ${referrerName},` : ''} on {referralDate}
      </footer>
    </section>
  );
};
